import React from 'react';
import { Link } from 'react-router-dom';
import { FaCog, FaMicrochip, FaCloud, FaBroadcastTower, FaNetworkWired, FaTools, FaBox } from 'react-icons/fa';
import './HeroFocusedHomePage.css';

function HeroFocusedHomePage() {
  const services = [
    {
      icon: <FaCog />,
      title: 'PLC & HMI Programming',
      description: 'Expert PLC & HMI programming in multiple languages (Structured Text, Ladder, Function Block Diagram, Sequential Function Chart) for industrial automation and process control. Multi Vendor support.',
      details: ['Siemens S7 PLCs', 'Allen-Bradley PLCs', 'Mitsubishi PLCs', 'Drives', 'LabVIEW', 'Wonderware']
    },
    {
      icon: <FaMicrochip />,
      title: 'Microchip Development',
      description: 'Advanced microcontroller development and embedded systems design for specialized industrial applications.',
      details: ['ARM Cortex', 'Espressif', 'PCB Design & Assembly', 'Real-time Systems', 'Custom Firmware', 'Power Optimization']
    },
    {
      icon: <FaCloud />,
      title: 'Cloud Computing',
      description: 'Cloud infrastructure solutions for data analytics, remote monitoring, and scalable industrial applications.',
      details: ['AWS Integration', 'Real-time Analytics', 'Secure Data Handling', 'Scalable Architecture']
    },
    {
      icon: <FaBroadcastTower />,
      title: 'IoT Devices',
      description: 'IoT device development and integration for smart factory automation and predictive maintenance.',
      details: ['Sensor Networks', 'MQTT Protocols', 'Edge Computing', 'Device Management']
    },
    {
      icon: <FaNetworkWired />,
      title: 'Networks',
      description: 'Advanced networking solutions for industrial communication and device connectivity across diverse protocols and standards.',
      details: ['WiFi & Bluetooth LE', 'Zigbee', 'RS485/RS232', 'Modbus ASCII/RTU', 'MTConnect', 'CANBus', 'Ethernet/IP', 'Profinet', 'OPC UA','CC-Link', 'Custom Protocols']
    },
    {
      icon: <FaBox />,
      title: 'Industrial Panels & Hardware',
      description: 'Complete electrical panel design, build, and installation services. From custom control enclosure design and schematic creation to on-site installation and refurbishment of existing systems.',
      details: ['Control Panel Design', 'Electrical Schematics', 'Custom Enclosure Build', 'Panel Assembly & Wiring', 'On-Site Installation', 'System Refurbishment', 'Safety Compliance Testing']
    },
    {
      icon: <FaTools />,
      title: 'Support & Breakdown Services',
      description: 'Rapid breakdown support and maintenance for industrial machines. 24/7 on-call availability, with multiskilled capabilities for comprehensive repairs.',
      details: ['24/7 On-Call Support', 'Emergency Breakdown Response', 'Multiskilled Repairs', 'Preventive Maintenance', 'Machine Diagnostics', 'Field Service']
    }
  ];
  return (
    <div style={{ background: '#060913', minHeight: '100vh' }}>
      <main>
        <section className="premium-hero" style={{ background: 'radial-gradient(circle at 50% 0%, #0f1424 0%, #060913 78%)', borderBottom: '1px solid rgba(0, 212, 255, 0.15)' }}>
          <style>{`
            .premium-hero {
              --premium-ink: #f4f7fb;
              --premium-muted: #9baabd;
              --premium-line: rgba(177, 201, 224, 0.18);
              --premium-cyan: #6de3e8;
              --premium-blue: #8aabff;
              position: relative;
              isolation: isolate;
              min-height: min(820px, calc(100vh - 72px));
              display: grid;
              place-items: center;
              overflow: hidden;
              padding: 120px 24px 140px;
              color: var(--premium-ink);
              background: #070b12;
              text-align: center;
            }
            .premium-hero::before, .premium-hero::after {
              content: '';
              position: absolute;
              inset: 0;
              pointer-events: none;
            }
            .premium-hero::before {
              z-index: -2;
              opacity: 0.55;
              background-image: linear-gradient(rgba(109, 227, 232, 0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(138, 171, 255, 0.08) 1px, transparent 1px);
              background-size: 72px 72px;
              mask-image: linear-gradient(to bottom, black, transparent 82%);
              animation: premium-grid-drift 22s linear infinite;
            }
            .premium-hero::after {
              z-index: -1;
              background: radial-gradient(circle at 50% 30%, rgba(76, 152, 255, 0.15), transparent 35%), radial-gradient(circle at 50% 70%, rgba(32, 199, 194, 0.08), transparent 40%), linear-gradient(135deg, rgba(7, 11, 18, 0.98), rgba(12, 23, 36, 0.84));
            }
            .premium-hero-inner {
              width: min(1120px, 100%);
              position: relative;
              z-index: 1;
            }
            .premium-hero-inner::before {
              content: '';
              position: absolute;
              width: 240px;
              height: 240px;
              top: -120px;
              left: 50%;
              transform: translateX(-50%);
              border: 1px solid rgba(109, 227, 232, 0.15);
              border-radius: 50%;
              box-shadow: 0 0 0 26px rgba(109, 227, 232, 0.02), 0 0 0 52px rgba(109, 227, 232, 0.01);
              animation: premium-orbit 12s ease-in-out infinite;
            }
            .premium-hero-content {
              max-width: 890px;
              margin: 0 auto;
              display: flex;
              flex-direction: column;
              align-items: center;
              animation: premium-rise 0.8s ease-out both;
            }
            .premium-hero-badge {
              display: inline-flex;
              align-items: center;
              gap: 10px;
              margin-bottom: 30px;
              padding: 9px 13px;
              border: 1px solid var(--premium-line);
              border-radius: 999px;
              color: var(--premium-cyan);
              background: rgba(111, 139, 166, 0.09);
              box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 12px 35px rgba(0, 0, 0, 0.18);
              font-size: 0.72rem;
              font-weight: 700;
              letter-spacing: 0.15em;
              text-transform: uppercase;
            }
            .premium-hero-badge::before {
              content: '';
              width: 7px;
              height: 7px;
              border-radius: 50%;
              background: var(--premium-cyan);
              box-shadow: 0 0 14px var(--premium-cyan);
            }
            .premium-hero h1 {
              max-width: 890px;
              margin: 0 auto;
              font-size: clamp(3rem, 6.5vw, 6.7rem);
              font-weight: 800;
              line-height: 1.05;
              letter-spacing: -0.055em;
              text-wrap: balance;
              text-align: center;
            }
            .premium-hero h1::first-line {
              color: #ffffff;
            }
            .premium-hero-description {
              max-width: 720px;
              margin: 34px auto 0 !important;
              padding: 20px 22px;
              border-left: none !important;
              border-top: 2px solid var(--premium-cyan);
              color: var(--premium-muted);
              background: rgba(13, 24, 37, 0.48);
              box-shadow: inset 0 10px 30px rgba(80, 171, 194, 0.02);
              font-size: clamp(1rem, 1.6vw, 1.18rem);
              line-height: 1.75;
              backdrop-filter: blur(14px);
              text-align: center;
            }
            .premium-hero-actions {
              display: flex;
              flex-wrap: wrap;
              gap: 14px;
              margin-top: 40px;
              justify-content: center;
            }
            .premium-hero-actions .btn {
              min-height: 52px;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              padding: 0 26px;
              border: 1px solid var(--premium-line);
              border-radius: 4px;
              font-size: 0.84rem;
              font-weight: 700;
              letter-spacing: 0.08em;
              text-decoration: none;
              text-transform: uppercase;
              transition: transform 180ms ease, border-color 180ms ease, background 180ms ease;
            }
            .premium-hero-actions .btn:hover {
              transform: translateY(-3px);
            }
            .premium-hero-actions .btn-primary {
              border-color: var(--premium-cyan);
              color: #061016;
              background: var(--premium-cyan);
              box-shadow: 0 12px 28px rgba(109, 227, 232, 0.18);
            }
            .premium-hero-actions .btn-secondary {
              color: var(--premium-ink);
              background: rgba(255, 255, 255, 0.04);
              backdrop-filter: blur(12px);
            }
            .premium-hero-actions .btn-secondary:hover {
              border-color: var(--premium-blue);
              background: rgba(138, 171, 255, 0.12);
            }
            @keyframes premium-rise {
              from { opacity: 0; transform: translateY(22px); }
              to { opacity: 1; transform: translateY(0); }
            }
            @keyframes premium-grid-drift {
              from { background-position: 0 0, 0 0; }
              to { background-position: 0 72px, 72px 0; }
            }
            @keyframes premium-orbit {
              0%, 100% { transform: translate(-50%, 0) rotate(0deg); opacity: 0.5; }
              50% { transform: translate(-50%, 14px) rotate(6deg); opacity: 0.85; }
            }
            @media (max-width: 640px) {
              .premium-hero {
                min-height: 720px;
                padding: 96px 20px 110px;
              }
              .premium-hero h1 {
                font-size: clamp(2.8rem, 14vw, 4.6rem);
              }
              .premium-hero-description {
                margin-top: 28px;
                padding: 17px 18px;
                font-size: 1rem;
              }
              .premium-hero-actions .btn {
                width: 100%;
              }
            }
            @media (prefers-reduced-motion: reduce) {
              .premium-hero::before, .premium-hero-inner::before, .premium-hero-content {
                animation: none;
              }
            }
          `}</style>
          <div className="premium-hero-inner">
            <div className="premium-hero-content">
              <span className="premium-hero-badge">Mission-Critical Engineering</span>
              <h1 style={{ fontWeight: 800 }}>Industrial Automation &amp; Embedded R&amp;D</h1>
              <p className="premium-hero-description">NAK Engineering designs resilient automation, embedded systems, and connected industrial platforms for teams working where precision, uptime, and intelligent control are non-negotiable.</p>
              <div className="premium-hero-actions">
                <Link to="/contact" className="btn btn-primary">Start a Technical Conversation</Link>
                <a href="#services" className="btn btn-secondary">View Engineering Capabilities</a>
              </div>
            </div>
          </div>
        </section>
        <section id="services" className="services-section">
          <div className="container">
            <h2>Our Expertise</h2>
            <div className="services-grid">
              {services.map((service, index) => (
                <div key={index} className="service-card-compact">
                  <div className="service-icon-small">{service.icon}</div>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <ul className="service-details">
                    {service.details.map((detail, i) => (
                      <li key={i}>{detail}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="about-section">
          <div className="container">
            <h2>Why NAK Engineering?</h2>
            <div className="about-grid">
              <div className="about-card">
                <h3>Experience</h3>
                <p>18+ years delivering industrial automation solutions across diverse manufacturing sectors</p>
              </div>
              <div className="about-card">
                <h3>Quality</h3>
                <p>Committed to excellence in design, implementation, and support of all automation projects</p>
              </div>
              <div className="about-card">
                <h3>Support</h3>
                <p>24/7 on-call support and maintenance to keep your operations running smoothly</p>
              </div>
            </div>
          </div>
        </section>

        <section className="cta-section">
          <div className="container">
            <h2>Ready to Transform Your Operations?</h2>
            <p>Let's discuss how we can help automate your industrial processes</p>
            <Link to="/contact" className="btn btn-primary btn-large">Contact Us Today</Link>
          </div>
        </section>
      </main>
    </div>
  );
}

export default HeroFocusedHomePage;
